import { CopyButton } from '@/_components/ui/copy-button'
import { TableCell } from '@/_components/ui/table'
import { computeDiff } from '@/_helpers/string-diff'
import { formatBRLFromReais } from '@doutorsim/shared/helpers/currency'
import type { TIncomeDeltaDTO } from '@doutorsim/shared/exposed/person-declarations'
import type { RowConfig } from '../_helpers/field-config'
import { formatRowValueForDisplay } from '../_helpers/formatting'
import { AtomicMismatch } from './atomic-mismatch'
import { IncomeDelta } from './income-delta'

interface PatientValueCellProps {
  row: RowConfig
  patientValue: string
  officialValue: string | null
  differs: boolean
  incomeDelta: TIncomeDeltaDTO | null
}

export const PatientValueCell = ({
  row,
  patientValue,
  officialValue,
  differs,
  incomeDelta,
}: PatientValueCellProps) => {
  const hasPatientValue = patientValue.length > 0
  // Copy payload is the formatted declared value only — the diff markup (added
  // / removed segments, income delta chip, AtomicMismatch tooltip) is display
  // chrome that shouldn't bleed into the clipboard.
  const copyValue =
    row.key === 'income' && hasPatientValue
      ? formatBRLFromReais(Number(patientValue))
      : hasPatientValue
        ? formatRowValueForDisplay(row, patientValue)
        : null

  return (
    <TableCell className="group relative border-l border-brand-grayscale-4 px-4 py-3 align-top">
      <div className="flex min-h-[1.5rem] items-center gap-2 pr-7 text-[13px] text-brand-grayscale-7">
        {hasPatientValue && (
          <>
            <span className="inline-flex flex-wrap items-center gap-x-0 tabular-nums">
              {row.key === 'income' ? (
                <span className="font-semibold">{formatBRLFromReais(Number(patientValue))}</span>
              ) : row.key === 'birth_date' ? (
                <AtomicMismatch
                  differs={differs}
                  display={patientValue}
                  tooltip="Datas não coincidem"
                />
              ) : row.subField === 'postcode' ? (
                <AtomicMismatch
                  differs={differs}
                  display={formatRowValueForDisplay(row, patientValue)}
                  tooltip="CEPs não coincidem"
                />
              ) : differs ? (
                computeDiff(
                  formatRowValueForDisplay(row, officialValue ?? ''),
                  formatRowValueForDisplay(row, patientValue),
                ).map((seg, i) =>
                  seg.type === 'equal' ? (
                    <span key={i}>{seg.text}</span>
                  ) : seg.type === 'added' ? (
                    <span
                      key={i}
                      className="bg-brand-green-primary-2 px-0.5 text-brand-green-primary-8"
                    >
                      {seg.text}
                    </span>
                  ) : (
                    <span
                      key={i}
                      className="bg-aux-red-danger-2 px-0.5 text-aux-red-danger-8 line-through decoration-aux-red-danger-5"
                    >
                      {seg.text}
                    </span>
                  ),
                )
              ) : (
                <span>{formatRowValueForDisplay(row, patientValue)}</span>
              )}
            </span>
            {row.key === 'income' && differs && incomeDelta && <IncomeDelta delta={incomeDelta} />}
          </>
        )}
      </div>
      {copyValue && (
        <CopyButton
          value={copyValue}
          className="absolute right-2 top-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100"
        />
      )}
    </TableCell>
  )
}
